import { Component, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { PrimaryContactInformationComponent } from './primary-contact-information/primary-contact-information.component';
import { ProcessingInformationComponent } from './processing-information/processing-information.component';
import { AccountStatus, MerchantAccount, MerchantAccountService, MerchantAccountType, MerchantAccountTypeComponent, CreditCardProcessingTechnologyCode, UniversityPerson } from '../../shared';

@Component({
    selector: 'app-account',
    templateUrl: './account.component.html',
    styleUrls: ['./account.component.css']
})
export class AccountComponent implements OnInit {

    @ViewChild(PrimaryContactInformationComponent) primaryContactInformationComponent: PrimaryContactInformationComponent;
    @ViewChild(ProcessingInformationComponent) processingInformationComponent: ProcessingInformationComponent;

    showForm = false;
    showLoading = false;
    showErrors = false;
    saving = false;
    merchantAccount: MerchantAccount;
    accountFormGroup: FormGroup;
    accountStatusClass: string;
    isCashnet: boolean;

    get acknowledgement(): AbstractControl {
        return this.accountFormGroup.get('acknowledgement');
    }
    get merchantAccountType(): AbstractControl {
        return this.accountFormGroup.get('merchantAccountType');
    }
    get generalInformation(): AbstractControl {
        return this.accountFormGroup.get('generalInformation');
    }
    get primaryContactInformation(): AbstractControl {
        return this.accountFormGroup.get('primaryContactInformation');
    }
    get mfkInformation(): AbstractControl {
        return this.accountFormGroup.get('mfkInformation');
    }
    get salesInformation(): AbstractControl {
        return this.accountFormGroup.get('salesInformation');
    }
    get processingInformation(): AbstractControl {
        return this.accountFormGroup.get('processingInformation');
    }
    get additionalInformation(): AbstractControl {
        return this.accountFormGroup.get('additionalInformation');
    }
    constructor(
        private readonly fb: FormBuilder,
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly toastr: ToastrService,
        private readonly modalService: NgbModal,
        private readonly merchantAccountService: MerchantAccountService
    ) {
    }

    ngOnInit() {
        this.route.data.subscribe(({ merchantAccount }: { merchantAccount: MerchantAccount }) => {
            this.merchantAccount = merchantAccount;
            this.accountStatusClass = this.getAccountStatusClass(merchantAccount.status);
            if (merchantAccount.id != null) {
                this.createForm();
                this.isCashnet = merchantAccount.merchantAccountType === MerchantAccountType.Cashnet;
                this.merchantAccountType.disable();
            } else {
                const modal = this.modalService.open(MerchantAccountTypeComponent, { size: 'lg', backdrop: 'static' });
                modal.result.then((merchantAccountType: MerchantAccountType) => {
                    this.merchantAccount.merchantAccountType = merchantAccountType;
                    this.isCashnet = merchantAccount.merchantAccountType === MerchantAccountType.Cashnet;
                    if (this.isCashnet) {
                        this.merchantAccount.processingInformation.creditCardProcessingTechnologyCd = CreditCardProcessingTechnologyCode.Website;
                    }
                    this.createForm();
                });
            }
        });
    }

    private createForm() {
        this.accountFormGroup = this.fb.group({
            acknowledgement: [this.merchantAccount.id != null, Validators.requiredTrue],
            merchantAccountType: [this.merchantAccount.merchantAccountType, Validators.required],
            generalInformation: this.fb.group({}),
            primaryContactInformation: this.fb.group({}),
            mfkInformation: this.fb.group({}),
            salesInformation: this.fb.group({}),
            processingInformation: this.fb.group({}),
            additionalInformation: [this.merchantAccount.additionalInfo]
        });
        if (!this.isStatusDraft()) {
            this.acknowledgement.disable();
        }
        this.showForm = true;
    }

    private getAccountStatusClass(status: AccountStatus): string {
        switch (status) {
        case AccountStatus.Draft:
                return "float-end badge text-bg-primary";
        case AccountStatus.Routing:
                return "float-end badge text-bg-danger";
        case AccountStatus.Complete:
                return "float-end badge text-bg-warning";
        case AccountStatus.Finalized:
                return "float-end badge text-bg-success";
        default:
                return "float-end badge text-bg-secondary";
        }
    }

    isStatusDraft(): boolean {
        return this.merchantAccount.status === AccountStatus.Draft;
    }

    isStatusRouting(): boolean {
        return this.merchantAccount.status === AccountStatus.Routing;
    }

    isStatusCompleteOrFinalized(): boolean {
        return this.merchantAccount.status === AccountStatus.Complete || this.merchantAccount.status === AccountStatus.Finalized;
    }

    onMerchantProfileChange(primaryContact: UniversityPerson) {
        this.primaryContactInformationComponent.primaryContactPerson.patchValue(primaryContact);
    }

    onCardAcceptanceChannelChange(disableWebsitePayment: boolean): void {
        if (!this.isStatusCompleteOrFinalized()) {
            this.processingInformationComponent.toggleDisableWebsiteCreditCardProcessingTechnology(disableWebsitePayment);
        }
    }

    saveAsDraft(): void {
        console.log(this.accountFormGroup);
        if (this.generalInformation.valid && this.primaryContactInformation.valid) {
            this.saving = true;
            if (this.merchantAccount.id != null) {
                this.updateAsDraft();
            } else {
                this.createAsDraft();
            }
        } else {
            this.toastr.error("There are errors on this Merchant Account that must be fixed before it can be saved.");
            this.showErrors = true;
        }
    }

    saveAndSubmit(): void {
        console.log(this.accountFormGroup);
        if (this.accountFormGroup.valid) {
            this.saving = true;
            if (this.merchantAccount.id != null) {
                this.updateAndSubmit();
            } else {
                this.createAndSubmit();
            }
        } else {
            this.toastr.error("There are errors on this Merchant Account that must be fixed before it can be saved.");
            this.showErrors = true;
        }
    }

    saveAndRestartWorkflow(): void {
        console.log(this.accountFormGroup);
        if (this.accountFormGroup.valid) {
            this.saving = true;
            this.updateAndRestart();
        } else {
            this.toastr.error("There are errors on this Merchant Account that must be fixed before it can be saved.");
            this.showErrors = true;
        }
    }

    save() {
        console.log(this.accountFormGroup);
        if (this.accountFormGroup.valid) {
            this.saving = true;
            this.update();
        } else {
            this.toastr.error("There are errors on this Merchant Account that must be fixed before it can be saved.");
            this.showErrors = true;
        }
    }

    private createAsDraft() {
        this.merchantAccountService.createAsDraft(this.accountFormGroup.getRawValue()).subscribe(
            (id) => {
                this.merchantAccount.id = id;
                this.toastr.success("The Merchant Account was successfully saved!");
                this.saving = false;
                this.router.navigate(['/account/mine']);
            },
            err => {
                this.saving = false;
                throw err;
            });
    }

    private updateAsDraft() {
        this.merchantAccountService.updateAsDraft(this.accountFormGroup.getRawValue(), this.merchantAccount.id).subscribe(
            () => {
                this.toastr.success("The Merchant Account was successfully saved!");
                this.saving = false;
                this.router.navigate(['/account/mine']);
            },
            err => {
                this.saving = false;
                throw err;
            });
    }

    private createAndSubmit() {
        this.merchantAccountService.createAndSubmit(this.accountFormGroup.getRawValue()).subscribe(
            (id) => {
                this.merchantAccount.id = id;
                this.toastr.success("The Merchant Account was successfully saved!");
                this.saving = false;
                this.router.navigate(['/account/mine']);
            },
            err => {
                this.saving = false;
                throw err;
            });
    }

    private updateAndSubmit() {
        this.merchantAccountService.updateAndSubmit(this.accountFormGroup.getRawValue(), this.merchantAccount.id).subscribe(
            () => {
                this.toastr.success("The Merchant Account was successfully saved!");
                this.saving = false;
                this.router.navigate(['/account/mine']);
            },
            err => {
                this.saving = false;
                throw err;
            });
    }

    private updateAndRestart() {
        this.merchantAccountService.updateAndRestart(this.accountFormGroup.getRawValue(), this.merchantAccount.id).subscribe(
            () => {
                this.toastr.success("The Merchant Account was successfully saved!");
                this.saving = false;
                this.router.navigate(['/account/mine']);
            },
            err => {
                this.saving = false;
                throw err;
            });
    }

    private update() {
        this.merchantAccountService.update(this.accountFormGroup.getRawValue(), this.merchantAccount.id).subscribe(
            () => {
                this.toastr.success("The Merchant Account was successfully saved!");
                this.saving = false;
                this.router.navigate(['/account/mine']);
            },
            err => {
                this.saving = false;
                throw err;
            });
    }
}
